import {
  ReportActivityArchivedRequest,
  ReportActivityDeleteRequest,
  ReportActivityReadRequest,
  ReportActivitySpamRequest,
  ReportActivitySummaryRequest
} from "../entities/reports/ReportActivitySummary/ReportActivitySummaryRequest";
import {
  ReportActivityCountResponse,
  ReportActivitySummaryResponse
} from "../entities/reports/ReportActivitySummary/ReportActivitySummaryResponse";
import { ActivityGetResponse } from "../pages/client/activity/entities/AcitivityGetResponse";
import { ActivityListRequest } from "../pages/client/activity/entities/AcitivityListRequest";
import { ActivityListResponse } from "../pages/client/activity/entities/AcitivityListResponse";
import {
  ActivityPendingResponse,
  WhatsAppRequestBody,
  WhatsAppRequestBodyFinalize,
  WhatsAppTypeSetResponse
} from "../pages/client/activity/entities/AcitivityPendingResponse";
import { ActivitySetComment } from "../pages/client/activity/entities/ActivitySetComment";
import { EmailEdit } from "../pages/client/components/EmailCompose";
import { InvoiceListFilters } from "../pages/client/invoice/InvoiceTableProvider";
import { TypeListRequest } from "../pages/inbox/InboxTableProvider";
import { IoActivityListResponse } from "../pages/reports/entities/IoActivityListResponse";
import { ToastService } from "../pages/shared/bootstrap/Toast";
import { TableHeader, TableHeaderWithFieldId } from "../pages/shared/Table";
import { restTimezone } from "../utils/ParseUtils";
import { handleErrorWithToast } from "../utils/RequestUtils";
import { SanitizeUtils } from "../utils/SanitizeUtils";
import CompanyService, { DataType } from "./CompanyService";
import ExportService from "./ExportService";
import FilterService from "./FilterService";
import RequestService from "./RequestService";
import { CacheEntity, StorageService } from "./StorageService";
import { TranslationService } from "./TranslationService";

interface SetSMSParams {
  personid: number;
  id?: number;
  content: string;
  phone: string;
  activitytypeid?: number;
  tagids?: string;
  groupid?: string | number;
}

interface PreviewEmailParams {
  email: EmailEdit;
  fileIds: string[];
  currentLanguage: string;
  personId: string;
  invoiceIds: number[];
  chkAllInv: boolean;
  invoiceFilters: InvoiceListFilters | undefined;
}

export enum EmailInvoiceListType {
  All = 2,
  Pending = 1,
  Due = 0,
  Claimables = 4,
  DueClaimables = 5,
  Selected = 6,
  Other = 3,
  SelectedList = 7
}

export type SetEmailRequest = {
  item: {
    MessageID?: number;
    includelink?: boolean;
    files: {
      id: string;
      name: string;
    }[];
    draft: boolean;
    content: string;
    personid: number;
    from: string;
    to: string;
    subject: string;
    copyme: boolean;
    groupid?: string;
    groupName?: string;
    fromname?: string;
    html: boolean;
    activitytypeid?: number;
    language?: string;
    cc?: string;
    bcc?: string;
    ccto?: string;
    bccto?: string;
    includeinvoices: boolean;
    listmode: "0" | "1" | null;
    mail_groupby?: number;
    mail_sortby?: string;
    reportexport?: number;
    mailframeid?: number;
    Tag_Message?: {
      TagID: number;
    }[];
    scheduleddate?: string;
    attach: boolean;
    includeattachment: boolean;
    IncludeAttachment: boolean;
    hasIO: boolean;
    replytoid: number | null;
    replacebody: "0" | "1";
    isexpanded?: boolean;
    quickfilter?: EmailInvoiceListType | null;
    filter?: string | null;
    ids?: string;
    orderby0: number | undefined;
    orderby1: number | undefined;
    orderby2: number | undefined;
    orderbyorderdesc0: "on" | "off";
    orderbyorderdesc1: "on" | "off";
    orderbyorderdesc2: "on" | "off";
    listtype?: string;
    templateattach?: number[];
    reply?: string | null;
    fowardFiles?: number[];
  };
};

class ActivityService {
  public static ActivityTypeFilterItems: { Label: string; Value: string }[] = [
    { Value: "4", Label: "WithManualActivities" },
    { Value: "0", Label: "WithEffectiveActivities" },
    { Value: "1", Label: "WithAutomaticActivities" },
    { Value: "5", Label: "AutomaticActivitiesOnly" },
    { Value: "2", Label: "WithUneffectiveActivities" },
    { Value: "3", Label: "NoActivities" }
  ];

  public static TypeFilterItems: { Label: string; Value: string }[] = [
    { Label: "FilterDefaultOptionLabel", Value: "" },
    { Label: "Mails", Value: "1" },
    { Label: "Comments", Value: "2" },
    { Label: "Tasks", Value: "3" },
    { Label: "SMS", Value: "4" },
    { Label: "TypeWhatsApp", Value: "6" },
    { Label: "OutgoingEmails", Value: "-2" },
    { Label: "BouncedEmail", Value: "-1" }
  ];

  public static async setComment(
    request: ActivitySetComment
  ): Promise<{ Response: string } | Error> {
    const result = (await RequestService.post("/activity/setcomment", {
      ...request,
      additionaldefinitions: CompanyService.getAdditionalDefinitions(),
      activitytypes: CompanyService.getActivityTypes() ?? []
    })) as { Response: string } | Error;
    if (!(result instanceof Error) && result.Response !== "Ok") {
      return new Error(result.Response);
    }
    return result;
  }
  public static async getList(request: ActivityListRequest | TypeListRequest) {
    const req: Promise<ActivityListResponse | Error> = RequestService.post(
      "/activity/list",
      {
        ...request,
        additionaldefinitions: CompanyService.getAdditionalDefinitions(),
        users: CompanyService.getUsers()
      }
    );
    req.then((result) => {
      if (result instanceof Error) {
        return;
      }
      result.list.forEach((x) => {
        if (x.groupId && x.groupName && x.groupName.length > 0) {
          StorageService.setCache(
            CacheEntity.GroupName,
            x.groupId,
            x.groupName
          );
        }
      });
    });
    return req;
  }
  public static async getInboxList(
    request: ActivityListRequest | TypeListRequest
  ) {
    const req: Promise<ActivityListResponse | Error> = RequestService.post(
      "/activity/inboxlist",
      {
        ...request,
        additionaldefinitions: CompanyService.getAdditionalDefinitions(),
        users: CompanyService.getUsers()
      }
    );
    req.then((result) => {
      if (result instanceof Error) {
        return;
      }
      result.list.forEach((x) => {
        if (x.groupId && x.groupName && x.groupName.length > 0) {
          StorageService.setCache(
            CacheEntity.GroupName,
            x.groupId,
            x.groupName
          );
        }
      });
    });
    return req;
  }
  public static async getActiveConversation(
    personId: string
  ): Promise<any | Error> {
    return RequestService.get(
      "/activity/getactiveconversation/" + personId,
      {}
    );
  }
  public static async getWhatsappChat(messageId: string): Promise<any | Error> {
    return RequestService.get("/activity/getwhatsappchat/" + messageId, {});
  }
  public static async finalizeConverzation(
    request: WhatsAppRequestBodyFinalize
  ): Promise<any | Error> {
    return RequestService.post("/activity/NotifyClientCloseConversation", {
      ...request
    });
  }
  public static async getSummary(
    request: ReportActivitySummaryRequest
  ): Promise<ReportActivitySummaryResponse | Error> {
    return RequestService.post("/activity/summary", {
      ...request,
      additionaldefinitions: CompanyService.getAdditionalDefinitions(),
      users: CompanyService.getUsers(),
      currencies: CompanyService.getCurrencies(),
      iostatus: CompanyService.getIoStatus()
    });
  }
  public static async getPending(
    personid: string,
    groupid?: number | string
  ): Promise<ActivityPendingResponse | Error> {
    return RequestService.post("/activity/pending", {
      personid,
      groupid,
      additionaldefinitions: CompanyService.getAdditionalDefinitions()
    });
  }
  public static async delete(id: string): Promise<unknown | Error> {
    return RequestService.post("/activity/delete", { id });
  }
  public static async setSMS(
    params: SetSMSParams
  ): Promise<{ Response: string; Id: number } | Error> {
    return RequestService.post("/activity/setsms", params);
  }
  public static async get(id: number): Promise<ActivityGetResponse | Error> {
    return RequestService.post("/activity/get", {
      id,
      additionaldefinitions: CompanyService.getAdditionalDefinitions()
    });
  }
  public static async aicategorize(
    id: number
  ): Promise<ActivityGetResponse | Error> {
    return RequestService.post("/activity/aicategorize", { id });
  }
  public static async setEmail(
    email: EmailEdit,
    personId: string,
    draft: boolean,
    loggedUserEmail: string,
    fileIds: string[],
    invoiceIds: number[]
  ): Promise<{ Response: string; Id: number } | Error> {
    let filter = null;
    switch (email.invoiceListType) {
      case EmailInvoiceListType.All:
        filter = FilterService.GetExtraFiltersRequestString(
          email.invoiceFilters
        );
        break;
      case EmailInvoiceListType.Selected:
        // if (checkAll) {
        //     filter = FilterService.GetExtraFiltersRequestString(invoiceFilters?.extraFilters);
        // }
        break;
    }

    let scheduledDateStr = undefined;
    if (email.programEmailDateTime) {
      const scheduledDate = new Date(email.programEmailDateTime);
      scheduledDateStr = `${scheduledDate.getFullYear()}-${scheduledDate.getMonth().toString().padStart(2, "0")}-${scheduledDate.getDay().toString().padStart(2, "0")} ${scheduledDate.getHours().toString().padStart(2, "0")}:${scheduledDate.getMinutes().toString().padStart(2, "0")}`;
    }
    const request: SetEmailRequest = {
      item: {
        MessageID: email.MessageID,
        files: fileIds.map((x) => ({ id: x, name: "" })),
        draft,
        content: email.bodyHtml,
        personid: parseInt(personId),
        from: loggedUserEmail,
        to: email.to,
        subject: email.subject,
        copyme: email.sendCopy,
        groupid: email.groupId,
        fromname: CompanyService.getUsers().find(
          (x) => x.Id === CompanyService.getUserid()
        )?.Value,
        html: true,
        activitytypeid: email.activityTypeId,
        language: email.invoiceLanguage,
        cc: email.toCC,
        bcc: email.toBCC,
        ccto: email.toCC,
        bccto: email.toBCC,
        mail_groupby: email.invoiceGroupBy
          ? parseInt(email.invoiceGroupBy)
          : undefined,
        reportexport: email.invoiceReportExportId,
        mailframeid: email.emailFrameId,
        Tag_Message: email.tags
          ?.split(",")
          .filter((x) => !!x)
          .map((x) => ({ TagID: parseInt(x) })),
        //replytoid
        scheduleddate: scheduledDateStr,
        attach: email.includeInvoiceList && email.invoiceListMode === "1",
        listmode:
          email.includeInvoiceList && email.invoiceListMode === "1" ? "1" : "0",
        listtype:
          email.includeInvoiceList && email.invoiceListMode === "1"
            ? email.invoiceListType.toString()
            : undefined,
        includeinvoices:
          email.includeInvoiceList && email.invoiceListMode === "1",
        includeattachment: email.includeInvoiceFiles,
        IncludeAttachment: email.includeInvoiceFiles,
        hasIO: email.includeInvoiceList,
        replytoid: email.replytoid,
        replacebody: email.replacebody,
        quickfilter:
          email.includeInvoiceList && email.invoiceListType
            ? email.invoiceListType
            : undefined,
        filter,
        ids:
          email.invoiceListType === EmailInvoiceListType.Selected
            ? invoiceIds.join(",")
            : undefined,
        orderby0: 0,
        orderby1: 0,
        orderby2: 0,
        orderbyorderdesc0: "off",
        orderbyorderdesc1: "off",
        orderbyorderdesc2: "off"
      }
    };
    return RequestService.post("/activity/setemail", {
      ...request,
      additionaldefinitions: CompanyService.getAdditionalDefinitions()
    });
  }

  public static async export(
    request: ActivityListRequest | TypeListRequest | { reid?: number },
    count: number
  ) {
    return ExportService.requestExport(
      "/activity/exportactivity",
      { ...request },
      count
    );
  }

  public static async exportMessageIo(messageId: number, ioCount: number) {
    return ExportService.requestExport(
      "/activity/exportmessageiolist",
      {
        id: messageId,
        additionaldefinitions: CompanyService.getAdditionalDefinitions()
      },
      ioCount
    );
  }

  public static async exportInboxList(
    request: ActivityListRequest | TypeListRequest,
    count: number
  ) {
    return ExportService.requestExport(
      "/activity/exportinboxlist",
      {
        ...request,
        additionaldefinitions: CompanyService.getAdditionalDefinitions(),
        users: CompanyService.getUsers()
      },
      count
    );
  }

  public static async exportIoActivityList(
    request: (ActivityListRequest | TypeListRequest) & { reid?: number },
    count?: number
  ) {
    return ExportService.requestExport(
      "/activity/exportioactivitylist",
      { ...request },
      count || 100,
      "ioactivitylist"
    );
  }

  public static async markAllAsRead() {
    await handleErrorWithToast(
      RequestService.post("/activity/markallasread", {})
    );
    ToastService.showToast(
      "Se generó un proceso asincrónico que al procesarse marcará todas los emails como leídos",
      undefined,
      "success"
    );
  }

  public static async setEmailNew(
    request: SetEmailRequest
  ): Promise<{ Response: string; Id: number } | Error> {
    const requestAux: SetEmailRequest = {
      item: {
        ...request.item,
        listmode:
          request.item.includeinvoices && request.item.listmode === null
            ? "0"
            : request.item.listmode,
        quickfilter:
          request.item.includeinvoices && request.item.quickfilter === null
            ? EmailInvoiceListType.Pending
            : request.item.quickfilter
      }
    };

    return RequestService.post("/activity/setemail", {
      ...requestAux,
      additionaldefinitions: CompanyService.getAdditionalDefinitions()
    });
  }

  public static async getMessageContentBounce(
    id: string
  ): Promise<{ body: string } | Error> {
    return RequestService.post("/activity/getmessagecontentbounce", { id });
  }

  public static async getEmailContent(request: {
    id: string;
    noheader: "0" | "1";
  }): Promise<{ Response: string; content: string } | Error> {
    return RequestService.post("/activity/getemailcontent", request);
  }
  public static async editEmail(
    messageId: number,
    sent: string,
    activityTypeId?: string,
    tags?: string,
    groupId?: string
  ): Promise<{ Response: string; id: number } | Error> {
    const request = {
      item: {
        messageid: messageId,
        sent: restTimezone(new Date(sent)).toJSON(),
        activitytypeid: activityTypeId ? parseInt(activityTypeId) : undefined,
        Tag_Message: tags
          ?.split(", ")
          .map((x) => ({ TagID: parseInt(x) }))
          .filter((x) => x.TagID),
        groupid: groupId
      }
    };
    return RequestService.post("/activity/setemailsent", request);
  }
  public static async previewEmailContent(
    request: SetEmailRequest
  ): Promise<{ Response: string; preview: string } | Error> {
    return RequestService.post("/activity/previewemail", {
      ...request,
      additionaldefinitions: CompanyService.getAdditionalDefinitions()
    });
  }
  public static async previewEmail({
    email,
    fileIds,
    currentLanguage,
    personId,
    invoiceIds,
    chkAllInv,
    invoiceFilters
  }: PreviewEmailParams): Promise<
    { Response: string; preview: string } | Error
  > {
    const request = {
      response: "",
      id: undefined,
      item: {
        files: fileIds.map((x) => ({ id: x })),
        draft: true,
        content: email.bodyHtml,
        personid: parseInt(personId),
        from: CompanyService.getUserEmail(),
        Fromname: CompanyService.getUsers().find(
          (x) => x.Id === CompanyService.getUserid()
        )?.Value,
        to: email.to,
        type: email.activityTypeId,
        subject: email.subject,
        copyme: email.sendCopy,
        groupid: email.groupId,
        html: true,
        activitytypeid: email.activityTypeId,
        language: currentLanguage,
        cc: email.toCC,
        bcc: email.toBCC,
        mail_groupby: email.invoiceGroupBy,
        reportexportid: email.invoiceReportExportId,
        mailframeid: email.emailFrameId,
        reply: "1",
        attach: email.includeInvoiceFiles,
        includeattachment: email.includeInvoiceFiles,
        IncludeAttachment: email.includeInvoiceFiles,
        Tag_Message: email.tags
          ?.split(",")
          .filter((x) => !!x)
          .map((x) => ({ TagID: parseInt(x) }))
      },
      ids: chkAllInv ? undefined : invoiceIds.join(","),
      filter: FilterService.GetExtraFiltersRequestString(
        chkAllInv ? invoiceFilters?.extraFilters : email.invoiceFilters
      ),
      quickfilter: email.invoiceListType
    };
    return RequestService.post("/activity/previewemail", {
      ...request,
      additionaldefinitions: CompanyService.getAdditionalDefinitions()
    });
  }

  public static getActivityHeadersStart() {
    const translate = TranslationService.translate;
    const fixedFields: TableHeaderWithFieldId[] = [
      {
        ...new TableHeader("Date", translate.Date, false, false, "w-120px"),
        fieldId: "date",
        type: DataType.Date
      },
      {
        ...new TableHeader("User", translate.User, false, false, "w-200px"),
        fieldId: "Username",
        type: DataType.Text
      }
    ];

    return fixedFields;
  }

  public static getActivityHeadersEnd() {
    const translate = TranslationService.translate;
    const fixedFields: TableHeaderWithFieldId[] = [
      {
        ...new TableHeader(
          "groupName",
          CompanyService.getGroupName(),
          false,
          false,
          "w-120px"
        ),
        fieldId: "groupName",
        type: DataType.Text
      },
      {
        ...new TableHeader(
          "ActivityTypeID",
          translate.ActivityType,
          false,
          false,
          "w-120px"
        ),
        fieldId: "ActivityTypeID",
        type: DataType.Text
      },
      {
        ...new TableHeader("typeName", translate.Type, false, false, "w-150px"),
        fieldId: "typeName",
        type: DataType.Text
      },

      {
        ...new TableHeader(
          "Content",
          translate.Content,
          false,
          false,
          "w-400px"
        ),
        fieldId: "Content",
        type: DataType.Text
      },
      {
        ...new TableHeader("tagList", translate.Tags, false, false, "w-150px"),
        fieldId: "tagList",
        type: DataType.Text
      },
      {
        ...new TableHeader(
          "hasAttachments",
          translate.Attachments,
          false,
          false,
          "w-80px"
        ),
        fieldId: "hasAttachments",
        type: DataType.Text
      },
      {
        ...new TableHeader("date2", translate.Created, false, false, "w-120px"),
        fieldId: "date2",
        type: DataType.Date
      }
    ];
    return fixedFields;
  }

  public static async getIoActivityList(
    request: ReportActivitySummaryRequest
  ): Promise<IoActivityListResponse | Error> {
    return RequestService.post("/activity/ioactivitylist", {
      ...request,
      additionaldefinitions: CompanyService.getAdditionalDefinitions(),
      currencies: CompanyService.getCurrencies(),
      users: CompanyService.getUsers(),
      iostatus: CompanyService.getIoStatus()
    });
  }
  public static async setWhatsApp(
    whatsAppRequest: WhatsAppRequestBody
  ): Promise<WhatsAppTypeSetResponse | Error> {
    return RequestService.post("/activity/setwhatsapp2", {
      ...whatsAppRequest,
      additionaldefinitions: CompanyService.getAdditionalDefinitions()
    });
  }
  public static async hasNewWhatsappMessage(
    personid: number,
    messageid: string,
    wamessageid: string
  ): Promise<{ hasnewwhatsappmessage: boolean } | Error> {
    return RequestService.post("/activity/HasNewWhatsappMessage", {
      personid,
      messageid,
      wamessageid
    });
  }
  public static async getUnreadCount(): Promise<
    ReportActivityCountResponse | Error
  > {
    return RequestService.post("/activity/getunreadcountinbox", {
      ...(CompanyService.canShowWhatsApp() && { whatsapp: "1" })
    });
  }
  public static async setRead(
    request: ReportActivityReadRequest
  ): Promise<any | Error> {
    // Opening a message must not change its read state in production mode
    if (!CompanyService.canActOnMessage()) {
      return undefined;
    }
    return RequestService.post("/activity/setread", {
      ...request,
      chkall: request.all ? "1" : "0",
      additionaldefinitions: CompanyService.getAdditionalDefinitions(),
      users: CompanyService.getUsers()
    }); // 0 unread 1 read
  }
  public static async setArchived(
    request: ReportActivityArchivedRequest
  ): Promise<any | Error> {
    return RequestService.post("/activity/setarchive", {
      ...request,
      chkall: request.all ? "1" : "0",
      additionaldefinitions: CompanyService.getAdditionalDefinitions(),
      users: CompanyService.getUsers()
    }); // 0 unread 1 read
  }
  public static replaceUsersTo(
    comment: string,
    replace: (user: string, id: string) => string
  ) {
    return comment.replaceAll(
      /@\[ {2}([\w|\W]+?) {2}\]\(\d+\)/g,
      replace("$1", "$2")
    );
  }
  public static replaceUsersToHtml(comment: string): string {
    if (!comment) return "";

    // Primero sanitizar el contenido original para prevenir XSS
    const sanitized = SanitizeUtils.sanitizeCommentHtml(comment);

    // Luego aplicar el reemplazo de usuarios
    return sanitized
      .replaceAll("\n", "<br/>")
      .split("@")
      .map((x, i) => {
        if (i === 0) {
          return x;
        }
        const user = CompanyService.getUsers()
          .filter((u) => x.startsWith(u.Value))
          .reduce(
            (prev, curr) =>
              (prev?.Value.length ?? 0) > curr.Value.length ? prev : curr,
            { Value: "" }
          );
        if (user.Value.length) {
          return `<span class='user-mention'>@${user.Value}</span>${x.substring(user.Value.length)}`;
        }
        return "@" + x;
      })
      .join("");
  }
  public static replaceUsersToText(comment: string): string {
    return comment;
  }
  public static async togglePinActivity(id: string): Promise<unknown | Error> {
    return RequestService.post("/activity/togglePinActivity", { id });
  }

  public static async setSpam(
    request: ReportActivitySpamRequest
  ): Promise<any | Error> {
    return RequestService.post("/activity/setspam", {
      ...request,
      chkall: request.all ? "1" : "0",
      additionaldefinitions: CompanyService.getAdditionalDefinitions(),
      users: CompanyService.getUsers()
    });
  }

  public static async deleteEmails(
    request: ReportActivityDeleteRequest
  ): Promise<any | Error> {
    return RequestService.post("/activity/deleteemails", {
      ...request,
      chkall: request.all ? "1" : "0",
      additionaldefinitions: CompanyService.getAdditionalDefinitions()
    });
  }

  public static async getCollector(
    messageId: string | number
  ): Promise<any | Error> {
    return RequestService.post("/activity/getcollector", { messageId });
  }

  public static async setCollector(
    messageId: string | number
  ): Promise<any | Error> {
    return RequestService.post("/activity/setcollector", { messageId });
  }

  public static async getCollectors(): Promise<any | Error> {
    return RequestService.post("/activity/getcollectors", {});
  }

  public static async getWAViewHistory(
    messageId: string | number
  ): Promise<{ Date: string; UserId: number; UserFullname: string }[] | Error> {
    return RequestService.post("/activity/getwaviewhistory", { messageId });
  }

  public static async getMailViewHistory(
    messageId: string | number
  ): Promise<{ Date: string; UserId: number; UserFullname: string }[] | Error> {
    return RequestService.post("/activity/getmailviewhistory", { messageId });
  }
}

export default ActivityService;
